/**
 * autismPADRouting.js
 *
 * Pure helpers for the Adult Autism Post-Assessment Discussion (PAD /
 * "Feedback Session") module. Mirrors autismDDRouting.js so the two PAD pages,
 * the shared sidebar, the Patient Overview card and the unit tests all agree on:
 *
 *   1. The two step routes (Assessment Outcome, then Next Steps).
 *   2. The live Diagnostic Decision outcome the module branches on (Met vs Not
 *      met) — always read from the DD stage, never duplicated into PAD.
 *   3. When each step is complete, the progress fraction written to the stage
 *      doc, the resume route, and the sidebar items (Next Steps is omitted
 *      entirely unless the outcome is Met).
 *
 * Like DD, everything here reads from `assessmentStages` (the object exposed by
 * ModularPatientDataContext) on the modular subcollection data model.
 */

/** Firestore stage key + report-section identifiers used across the PAD module. */
export const AUTISM_PAD_STAGE_KEY = 'postAssessmentDiscussion';
/** Backend / Cloud Function report-stage name — do not use for clinician-facing copy. */
export const AUTISM_PAD_REPORT_STAGE = 'Autism Post-Assessment Discussion';
export const AUTISM_PAD_OUTCOME_SECTION = 'Assessment Outcome';

/**
 * Clinician-facing module name for The Owl Centre Autism workflow.
 * "Post-Assessment Discussion" remains the TD-internal / ADHD name and the
 * backend report-stage key above; only Autism PAD UI surfaces should use this.
 */
export const AUTISM_PAD_DISPLAY_NAME = 'Feedback Session';

// ─── Route helpers ──────────────────────────────────────────────────────────

/** Builds the PAD module base path for a patient (no trailing slash). */
export const getAutismPADBasePath = (patientId) =>
  `/patient/${patientId}/proforma/adult-asd/post-assessment-discussion`;

/**
 * Returns the absolute path for each PAD step, keyed by step id. Used by the
 * pages (CTA navigation), the sidebar and routes.js.
 */
export const getAutismPADStepRoutes = (patientId) => {
  const base = getAutismPADBasePath(patientId);
  return {
    assessmentOutcome: `${base}/assessment-outcome`,
    nextSteps: `${base}/next-steps`,
  };
};

// ─── Diagnostic Decision outcome (read live, never duplicated) ────────────────

/**
 * Reads the Met/Not-met outcome recorded on the Diagnostic Decision stage. PAD
 * branches on this (goals + Next Steps only exist when the patient Met criteria)
 * but never stores its own copy, so a late DD change is always reflected.
 *
 * @param {Object|undefined} assessmentStages - modular stages object
 * @returns {string} 'Met' | 'Not met' | '' (when not yet decided)
 */
export function getAutismDiagnosisOutcome(assessmentStages) {
  return assessmentStages?.diagnosticDecision?.data?.autismDecision?.outcome || '';
}

/** Whether the Next Steps step applies — only when the patient Met criteria. */
export function isNextStepsApplicable(outcome) {
  return outcome === 'Met';
}

// ─── Completeness ─────────────────────────────────────────────────────────────

/** Whether a structured-notes array has at least one non-empty note. */
function hasStructuredNotes(notes) {
  if (!Array.isArray(notes)) return false;
  return notes.some((n) => n && typeof n.text === 'string' && n.text.trim());
}

/** The four Next Steps support toggles, in display order. */
export const PAD_NEXT_STEPS_KEYS = [
  'relationshipAndPartnerSupport',
  'employmentSupport',
  'academicSupport',
  'supportForAutisticWomen',
];

/**
 * Whether the Assessment Outcome step is complete enough to leave.
 *
 *   - Met:     the patient-goals notes must have content (the only mandatory
 *              free-text), because they feed the goals paragraph.
 *   - Not met: nothing is mandatory (the discussed checkboxes are optional, like
 *              the DD criteria), so the clinician can finish straight away.
 *
 * @param {Object|undefined} data - assessmentStages.postAssessmentDiscussion.data
 * @param {string} outcome - the DD outcome
 * @returns {boolean}
 */
export function isAssessmentOutcomeComplete(data, outcome) {
  if (outcome === 'Met') {
    return hasStructuredNotes(data?.assessmentOutcome?.patientGoalsNotes);
  }
  return true;
}

/**
 * Whether every one of the four Next Steps toggles has been answered (each must
 * be an explicit true/false — null means "not yet answered").
 *
 * @param {Object|undefined} nextSteps - data.nextSteps
 * @returns {boolean}
 */
export function isNextStepsComplete(nextSteps) {
  const ns = nextSteps || {};
  return PAD_NEXT_STEPS_KEYS.every((key) => ns[key] === true || ns[key] === false);
}

// ─── Progress ─────────────────────────────────────────────────────────────────

/**
 * Computes PAD progress as a fraction (0..1). A completed stage always reports 1
 * (the Finish flow forces it). Otherwise:
 *   - Not met (or undecided): the single Assessment Outcome step — 1 once the
 *     clinician has confirmed they explained the outcome, else 0.
 *   - Met: the average of two equally-weighted parts — patient-goals notes
 *     present, and all four Next Steps toggles answered.
 *
 * @param {Object} params
 * @param {Object} params.data - assessmentStages.postAssessmentDiscussion.data
 * @param {string} params.outcome - the DD outcome
 * @param {string} [params.status] - stage status; 'completed' forces 1
 * @returns {number} Progress fraction between 0 and 1
 */
export function computeAutismPADProgress({ data, outcome, status }) {
  if (status === 'completed') return 1;
  const d = data || {};

  if (outcome !== 'Met') {
    return d.assessmentOutcome?.discussed?.explainedOutcome ? 1 : 0;
  }

  const goalsDone = hasStructuredNotes(d.assessmentOutcome?.patientGoalsNotes) ? 1 : 0;
  const nextStepsDone = isNextStepsComplete(d.nextSteps) ? 1 : 0;
  return (goalsDone + nextStepsDone) / 2;
}

// ─── Resume routing ───────────────────────────────────────────────────────────

/**
 * Computes which PAD step the "Start / Continue" action should land on. The
 * clinician always begins at the Assessment Outcome; only once that is complete
 * (Met path, goals written) and Next Steps remains unfinished do we resume them
 * directly on Next Steps.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {Object|undefined} params.assessmentStages - modular stages object
 * @returns {string} Absolute route path for the resume step
 */
export function getAutismPADStartRoute({ patientId, assessmentStages }) {
  const routes = getAutismPADStepRoutes(patientId);
  const stage = assessmentStages?.[AUTISM_PAD_STAGE_KEY];
  const data = stage?.data || {};
  const outcome = getAutismDiagnosisOutcome(assessmentStages);

  if (
    isNextStepsApplicable(outcome) &&
    isAssessmentOutcomeComplete(data, outcome) &&
    !isNextStepsComplete(data.nextSteps)
  ) {
    return routes.nextSteps;
  }

  return routes.assessmentOutcome;
}

// ─── Sidebar sections ───────────────────────────────────────────────────────

export const AUTISM_PAD_STEP_TITLES = {
  assessmentOutcome: 'Assessment Outcome',
  nextSteps: 'Next Steps',
};

/**
 * The PAD step ids in display order. Next Steps only exists when the outcome is
 * Met, so the array is length 1 or 2 — the same index the pages/sidebar use.
 *
 * @param {string} outcome - the DD outcome
 * @returns {string[]} ordered step ids
 */
export function getAutismPADOrderedStepIds(outcome) {
  return isNextStepsApplicable(outcome)
    ? ['assessmentOutcome', 'nextSteps']
    : ['assessmentOutcome'];
}

/**
 * Builds the shared <ModularSideNavbar> items for the PAD module. Next Steps is
 * omitted entirely when the outcome is not Met. Mirrors buildAutismDDNavItems.
 *
 * @param {Object} params
 * @param {Object|undefined} params.assessmentStages - modular stages object
 * @param {number} params.currentSectionIndex - zero-based active page index
 * @returns {{items: Array, orderedIds: string[], activeId: string}}
 */
export function buildAutismPADNavSections({ assessmentStages, currentSectionIndex }) {
  const stage = assessmentStages?.[AUTISM_PAD_STAGE_KEY];
  const data = stage?.data || {};
  const stageComplete = stage?.status === 'completed';
  const outcome = getAutismDiagnosisOutcome(assessmentStages);

  const orderedIds = getAutismPADOrderedStepIds(outcome);
  const outcomeComplete = isAssessmentOutcomeComplete(data, outcome);
  const nextStepsComplete = isNextStepsComplete(data.nextSteps);

  const items = [
    {
      id: 'assessmentOutcome',
      badge: 1,
      label: AUTISM_PAD_STEP_TITLES.assessmentOutcome,
      status: (outcomeComplete || stageComplete) ? 'completed' : 'default',
      isUnlocked: true,
    },
  ];

  if (isNextStepsApplicable(outcome)) {
    items.push({
      id: 'nextSteps',
      badge: 2,
      label: AUTISM_PAD_STEP_TITLES.nextSteps,
      // Unlocked once the Assessment Outcome (goals) is complete.
      isUnlocked: outcomeComplete,
      status: (nextStepsComplete || stageComplete) ? 'completed' : 'default',
    });
  }

  return { items, orderedIds, activeId: orderedIds[currentSectionIndex] };
}
